// frontend/src/utils/estimateTotals.ts

export interface EstimateTotalsResult {
  preDiscountSubtotal: number;
  totalDiscounts: number;
  discountedSubtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
}

interface LineItemForTotals {
  id?: number;
  total_price: number;
  labor_hours?: number | null;
  hourly_rate?: number | null;
  labor_discount_percentage?: number | null;
  labor_discount_amount?: number | null;
  parts?: Array<{
    quantity: number;
    unit_price: number;
    discount_percentage?: number | null;
    discount_amount?: number | null;
  }>;
}

/**
 * Calculate estimate totals with correct pre-discount subtotal.
 *
 * Pre-discount subtotal = sum of (labor_hours * hourly_rate) + (parts qty * unit_price)
 * for each line item, before any discounts.
 *
 * Discounts = sum of all labor + part discount amounts.
 *
 * Discounted subtotal = pre-discount - discounts (cross-checked against sum of total_price).
 *
 * @param lineItems - all line items on the estimate
 * @param taxRate - tax rate as a percentage (e.g. 8.25)
 * @param lineItemStatus - optional map of lineItemId -> 'approved'|'declined'|'unselected'.
 *                         When provided with at least one 'approved', only approved items are included.
 */
export function calculateEstimateTotals(
  lineItems: LineItemForTotals[],
  taxRate: number,
  lineItemStatus?: Record<number, string>
): EstimateTotalsResult {
  const hasApproved = lineItemStatus
    ? Object.values(lineItemStatus).some(s => s === 'approved')
    : false;

  const includedItems = lineItems.filter(item => {
    if (!hasApproved || !lineItemStatus) return true;
    return lineItemStatus[item.id!] === 'approved';
  });

  let preDiscountSubtotal = 0;
  let totalDiscounts = 0;

  for (const item of includedItems) {
    // Labor: raw = hours * rate
    const laborHours = item.labor_hours || 0;
    const hourlyRate = item.hourly_rate || 0;
    const rawLabor = laborHours * hourlyRate;

    // Labor discount
    let laborDiscount = 0;
    if (rawLabor > 0) {
      laborDiscount = item.labor_discount_amount
        || (rawLabor * (item.labor_discount_percentage || 0) / 100);
    }

    // Parts: raw = sum of qty * unit_price
    let rawParts = 0;
    let partsDiscount = 0;
    if (item.parts && item.parts.length > 0) {
      for (const part of item.parts) {
        const basePartCost = part.quantity * part.unit_price;
        rawParts += basePartCost;
        partsDiscount += part.discount_amount
          || (basePartCost * (part.discount_percentage || 0) / 100);
      }
    }

    preDiscountSubtotal += rawLabor + rawParts;
    totalDiscounts += laborDiscount + partsDiscount;
  }

  preDiscountSubtotal = Math.round(preDiscountSubtotal * 100) / 100;
  totalDiscounts = Math.round(totalDiscounts * 100) / 100;
  const discountedSubtotal = Math.round((preDiscountSubtotal - totalDiscounts) * 100) / 100;
  const taxAmount = Math.round((discountedSubtotal * taxRate / 100) * 100) / 100;
  const total = Math.round((discountedSubtotal + taxAmount) * 100) / 100;

  return {
    preDiscountSubtotal,
    totalDiscounts,
    discountedSubtotal,
    taxRate,
    taxAmount,
    total,
  };
}
